import React, { useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { becomeVendor } from "../../api/userApi";
import { loginUser } from "../../redux/actions";
import "./VendorOnboarding.css";

const ONBOARDING_DONE_KEY = "vendor_onboarding_done";
const SUCCESS_DELAY_MS = 2000;

const SERVICE_TYPE_OPTIONS = [
  { value: "GroupTour", label: "Group trips" },
  { value: "SharedRental", label: "Shared rentals" },
  { value: "PartnerService", label: "Private tours" },
];

const schema = yup.object().shape({
  full_name: yup.string().trim().required("Full name is required"),
  company_name: yup.string().trim().required("Company name is required"),
  contact_number: yup.string().trim().required("Contact number is required"),
  khalti_number: yup.string().trim().required("Khalti number is required"),
  service_types: yup
    .array()
    .min(1, "Select at least one service you provide")
    .required("Select at least one service"),
  website: yup.string().trim().url("Enter a valid URL").nullable(),
  address: yup.string().trim().nullable(),
});

function VendorOnboarding({ currentUser, loginUser }) {
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const history = useHistory();

  if (!currentUser || currentUser.email === undefined) {
    return <Redirect to={{ pathname: "/login", state: { from: { pathname: "/vendor/onboarding" } } }} />;
  }
  if (currentUser.role === "Vendor" && currentUser.vendor?.id) {
    return <Redirect to="/partner" />;
  }

  if (showSuccess) {
    return (
      <div className="vendor-onboarding-page">
        <div className="vendor-onboarding-card vendor-onboarding-card--success">
          <div className="vendor-onboarding-success-icon" aria-hidden="true">🎉</div>
          <h1 className="vendor-onboarding-title">You&apos;re all set!</h1>
          <p className="vendor-onboarding-success-text">Redirecting to your partner dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-onboarding-page">
      <div className="vendor-onboarding-card">
        <h1 className="vendor-onboarding-title">Partner Information</h1>
        <p className="vendor-onboarding-sub">Just a few details so we can contact you.</p>

        <Formik
          initialValues={{
            full_name: currentUser.name || "",
            company_name: "",
            contact_number: "",
            khalti_number: "",
            service_types: [],
            website: "",
            address: "",
          }}
          validationSchema={schema}
          onSubmit={async (values) => {
            setSubmitError(null);
            try {
              const res = await becomeVendor({
                full_name: values.full_name,
                company_name: values.company_name,
                contact_number: values.contact_number,
                khalti_number: values.khalti_number,
                assignment_types: (values.service_types || []).length ? values.service_types.join(",") : undefined,
                website: values.website || undefined,
                address: values.address || undefined,
              });
              const user = res.data?.user;
              if (user) loginUser(user);
              try {
                sessionStorage.setItem(ONBOARDING_DONE_KEY, "1");
              } catch (_) {}
              setShowSuccess(true);
              setTimeout(() => history.push("/partner"), SUCCESS_DELAY_MS);
            } catch (err) {
              const msg = err?.response?.data?.errors;
              setSubmitError(Array.isArray(msg) ? msg.join(" ") : msg || "Something went wrong. Please try again.");
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="vendor-onboarding-form">
              {submitError && (
                <div className="vendor-onboarding-error" role="alert">
                  {submitError}
                </div>
              )}

              <div className="vendor-onboarding-field">
                <label htmlFor="full_name">Full name *</label>
                <Field id="full_name" name="full_name" type="text" placeholder="Your full name" className="vendor-onboarding-input" />
                <ErrorMessage name="full_name" component="span" className="vendor-onboarding-field-error" />
              </div>

              <div className="vendor-onboarding-field">
                <label htmlFor="company_name">Company name *</label>
                <Field id="company_name" name="company_name" type="text" placeholder="Company or business name" className="vendor-onboarding-input" />
                <ErrorMessage name="company_name" component="span" className="vendor-onboarding-field-error" />
              </div>

              <div className="vendor-onboarding-field">
                <label htmlFor="contact_number">Contact number *</label>
                <Field id="contact_number" name="contact_number" type="text" placeholder="e.g. 98XXXXXXXX" className="vendor-onboarding-input" />
                <ErrorMessage name="contact_number" component="span" className="vendor-onboarding-field-error" />
              </div>

              <div className="vendor-onboarding-field">
                <label htmlFor="khalti_number">Khalti number *</label>
                <Field id="khalti_number" name="khalti_number" type="text" placeholder="For payouts" className="vendor-onboarding-input" />
                <p className="vendor-onboarding-helper">We use this to send your payments instantly.</p>
                <ErrorMessage name="khalti_number" component="span" className="vendor-onboarding-field-error" />
              </div>

              <div className="vendor-onboarding-field vendor-onboarding-field--services">
                <label id="services-label" className="vendor-onboarding-label">Services you provide *</label>
                <div className="vendor-onboarding-checkbox-grid" role="group" aria-labelledby="services-label">
                  {SERVICE_TYPE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="vendor-onboarding-checkbox">
                      <input
                        type="checkbox"
                        name="service_types"
                        value={opt.value}
                        checked={(values.service_types || []).includes(opt.value)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...(values.service_types || []), opt.value]
                            : (values.service_types || []).filter((v) => v !== opt.value);
                          setFieldValue("service_types", next);
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage name="service_types" component="span" className="vendor-onboarding-field-error" />
              </div>

              <div className="vendor-onboarding-field">
                <label htmlFor="website">Website (optional)</label>
                <Field id="website" name="website" type="url" placeholder="https://..." className="vendor-onboarding-input" />
                <ErrorMessage name="website" component="span" className="vendor-onboarding-field-error" />
              </div>

              <div className="vendor-onboarding-field">
                <label htmlFor="address">Address (optional)</label>
                <Field id="address" name="address" type="text" placeholder="City, area" className="vendor-onboarding-input" />
                <ErrorMessage name="address" component="span" className="vendor-onboarding-field-error" />
              </div>

              <button type="submit" className="vendor-onboarding-submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : "Continue to Dashboard"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

const mapStateToProps = ({ userStore }) => ({ currentUser: userStore.currentUser });
export default connect(mapStateToProps, { loginUser })(VendorOnboarding);
